/**
 * Helper: Format date
 * -----------------------------------------------------------------------------
 * Returns formatted date based on Ruby format.
 *
 * @param {String} path - Path to `date_format` string or Ruby formatted date,
 * if the `date_format` exists it will be automatically added to
 * `theme-compiled-strings.liquid`
 * @param {Object} date - Date object returned from Date() constructor.
 * @param {Object} [config] - Date output config.
 * @param {Boolean} [config.startMonday] - Day of week starts on Monday.
 * @returns {String}
 *
 */
/* eslint-disable line-comment-position, no-inline-comments */
export default (path, date, config) => {
  const dateString = path.includes('date_formats') ? findDateString(path) : path
  const dateObject = buildDateObject(date, config)

  /**
   * Replace Ruby timecodes.
   * - https://strftime.net/
   * - Not all timecodes are implemented.
   */
  let formattedDate = dateString
    .replaceAll('%S', dateObject.second) // Second - 00..59
    .replaceAll('%M', dateObject.minute) // Minute - 00..59
    .replaceAll('%k', dateObject.hour) // Hour - 0..23
    .replaceAll('%H', dateObject.hourPadded) // Hour - 00..23
    .replaceAll('%l', dateObject.hour12) // Hour - 0..12
    .replaceAll('%I', dateObject.hour12Padded) // Hour - 00..12
    .replaceAll('%e', dateObject.day) // Day - 1..31
    .replaceAll('%d', dateObject.dayPadded) // Day - 01..31
    .replaceAll('%u', dateObject.weekDay) // Day of week - 1..7
    .replaceAll('%m', dateObject.monthPadded) // Month - 01..12
    .replaceAll('%b', dateObject.monthShort) // Month - Jan..Dec
    .replaceAll('%B', dateObject.monthLong) // Month - January..December
    .replaceAll('%y', dateObject.yearShort) // Year - 23
    .replaceAll('%Y', dateObject.year) // Year - 2023
    .replaceAll('%C', dateObject.century) // Century - 20 in 2023
    .replaceAll('%P', dateObject.denomination) // Denomination - am..pm
    .replaceAll('%p', dateObject.denominationUpper) // Denomination - AM..PM
    .replaceAll('%z', dateObject.timezone) // Time zone - +0100
    .replaceAll('%Z', dateObject.timezoneName) // Time zone - British Summer Time

  /**
   * Grouped Ruby timecodes.
   */
  formattedDate = formattedDate
    .replaceAll('%D', dateObject.date) // Locale away date DD/MM/YY or MM/DD/YY
    .replaceAll('%v', dateObject.vmsDate) // DD-mmm-YYYY
    .replaceAll('%F', dateObject.isoDate) // YYYY-MM-DD
    .replaceAll('%r', dateObject.time12) // hh:MM:SS AM
    .replaceAll('%R', dateObject.time24) // HH:MM
    .replaceAll('%T', dateObject.time24Seconds) // HH:MM:SS

  /**
   * Return date.
   */
  return formattedDate
}

/**
 * Find date string in compiled strings.
 * - Only runs when `date_formats` is in path.
 * @param {String} path - Path to `date_format` string.
 * @returns {String}
 */
function findDateString(path) {
  let compiledString = cnvs.compiledStrings || {}
  let customString = cnvs.strings || {}
  let missingString = false

  for (const part of path.split('.')) {
    if (!customString[part] && !compiledString[part]) {
      missingString = true
      break
    }

    compiledString = compiledString[part] ? compiledString[part] : false
    customString = customString[part] ? customString[part] : false
  }

  /**
   * Define string, cnvs.strings should override cnvs.compiledStrings.
   */
  let string = false

  if (typeof compiledString === 'string') {
    string = compiledString
  }

  if (typeof customString === 'string') {
    string = customString
  }

  /**
   * Throw error if no string found
   */
  if (missingString || !string) {
    window.console.warn(`No date format found at 'cnvs.compiledStrings.${path}' or 'cnvs.strings.${path}'`)
    return 'No date format found'
  }

  return string
}

/**
 * Build date object.
 * @param {Object} date - Date object returned from Date() constructor.
 * @param {Object} [config] - Date output config.
 * @returns {Object}
 */
function buildDateObject(date, {
  startMonday = true,
} = {}) {
  const locale = cnvs.store.language
  const hour = date.getHours()
  const month = date.getMonth() + 1
  let weekDay = date.getDay() + 1
  const denomination = hour > 11 ? 'pm' : 'am'

  if (startMonday) {
    weekDay = date.getDay() === 0 ? 7 : date.getDay()
  }

  /**
   * Set base timecodes.
   */
  const tc = {
    second: getPadded(date.getSeconds()),
    minute: getPadded(date.getMinutes()),
    hour,
    hourPadded: getPadded(hour),
    hour12: hour % 12,
    hour12Padded: getPadded(hour % 12),
    day: date.getDate(),
    dayPadded: getPadded(date.getDate()),
    weekDay,
    monthLong: date.toLocaleString(locale, { month: 'long' }),
    monthPadded: getPadded(month),
    monthShort: date.toLocaleString(locale, { month: 'short' }),
    year: date.getFullYear(),
    yearShort: String(date.getFullYear()).slice(-2),
    century: String(date.getFullYear()).slice(0, 2),
    denomination,
    denominationUpper: denomination.toUpperCase(),
    timezone: getTimezoneOffset(date, locale),
    timezoneName: getTimezoneName(date, locale),
  }

  /**
   * Grouped timecodes.
   */
  tc.date = date.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' })
  tc.vmsDate = `${tc.dayPadded}-${tc.monthShort}-${tc.year}`
  tc.isoDate = `${tc.year}-${tc.monthPadded}-${tc.dayPadded}`
  tc.time12 = `${tc.hour12Padded}:${tc.minute}:${tc.second} ${tc.denominationUpper}`
  tc.time24 = `${tc.hourPadded}:${tc.minute}`
  tc.time24Seconds = `${tc.hourPadded}:${tc.minute}:${tc.second}`

  if (cnvs.storybook) {
    window.console.log('Timecodes', tc)
  }

  /**
   * Return date object.
   */
  return tc
}

/**
 * Get time zone offset in Ruby format.
 * - Locale string doesn't just return the time zone.
 * @param {Number} date - Date object.
 * @param {String} locale - Locale to use.
 * @returns {Number}
 */
function getTimezoneOffset(date, locale) {
  let timezone = date.toLocaleString(locale, { day: '2-digit', timeZoneName: 'longOffset' })
  timezone = timezone.match(/[+-]{1}\d{2}:\d{2}/g)

  if (!timezone?.length) {
    return '+0000'
  }

  return timezone[0].replace(':', '')
}

/**
 * Get time zone name.
 * @param {Number} date - Date object.
 * @param {String} locale - Locale to use.
 * @returns {String}
 */
function getTimezoneName(date, locale) {
  const timezone = date.toLocaleString(locale, { day: '2-digit', timeZoneName: 'long' })
  return timezone.slice(4)
}

/**
 * Get padded value.
 * @param {Number} value - Value to pad.
 * @returns {Number}
 */
function getPadded(value) {
  return value < 10 ? `0${value}` : value
}
